import React from "react";

import { IgTypography } from "@intelligems/ig-theme";
import { GraphConfig, GraphType } from "@intelligems/ig-types";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useSelector } from "@src/store";
import { getExperienceAnalyticsPageName } from "@src/store/slices/dashboardFilters";
import { getShareSettings } from "@src/store/slices/global";
import { theme } from "@src/theme";

import {
  BarMetricData,
  GraphMetricData,
  StackedBarMetricData,
} from "../../../../types/analytics";
import { calculateEstImpactTb } from "../utils";

interface EstImpactProps {
  compareGroupA: string;
  compareGroupB: string;
  config: GraphConfig;
  groupMetricData: GraphMetricData;
  isSharing?: boolean;
  testDuration: number;
}

export const EstImpact = ({
  compareGroupA,
  compareGroupB,
  config,
  groupMetricData,
  isSharing,
  testDuration,
}: EstImpactProps) => {
  const shareSettings = useSelector(getShareSettings());
  const pageName = useSelector(getExperienceAnalyticsPageName);

  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const renderEstImpact = (config: GraphConfig) => {
    const compareRecordA = groupMetricData.find(
      (data) => data.variation_id === compareGroupA
    );
    const compareRecordB = groupMetricData.find(
      (data) => data.variation_id === compareGroupB
    );

    if (!compareRecordA || !compareRecordB) return;

    let aValue: number;
    let bValue: number;

    if (config.graphType === GraphType.StackedBar) {
      aValue = (compareRecordA as StackedBarMetricData).impactMetricVal ?? 0;
      bValue = (compareRecordB as StackedBarMetricData).impactMetricVal ?? 0;
    } else {
      aValue = (compareRecordA as BarMetricData).value ?? 0;
      bValue = (compareRecordB as BarMetricData).value ?? 0;
    }

    const contextKey = config.denominatorMetric ?? "n_visitors";
    const totalContextCount = groupMetricData.reduce(
      (sum, record) => sum + (((record as any)[contextKey] as number) ?? 0),
      0
    );

    return calculateEstImpactTb(
      config,
      totalContextCount,
      aValue,
      bValue,
      storeCurrencyConfig!.value,
      testDuration,
      pageName
    );
  };

  const impact = renderEstImpact(config) || "";
  if (isSharing && !shareSettings.showImpact) {
    return (
      <Grid sx={{ textAlign: "start", minWidth: "150px" }}>
        <IgTypography>
          EST. <Box component={"span"}>XXX.XX</Box>{" "}
          {impact.substring(impact.indexOf(" ") + 1)}
        </IgTypography>
      </Grid>
    );
  }

  if (pageName !== "overview") return null;

  return (
    <Grid
      sx={{
        textAlign: "start",
        minWidth: "150px",
        minHeight: "23px",
      }}
    >
      {!config.hasImpactCalculation ? null : (
        <IgTypography
          variant={"body2"}
          sx={{ color: theme.palette.text.secondary }}
        >
          {impact === "" ? "" : "EST."} {impact}
        </IgTypography>
      )}
    </Grid>
  );
};
